export type DeviceItem = {
  id: number;
  dev_eui: string;
  device_name: string;
  device_model: string;
  device_profile_key: string;
  app_key?: string | null;
  battery_level: number | null;
  reporting_interval_seconds: number;
  asset_id: number | null;
  last_seen_at: string | null;
  created_at: string;

  company_id?: number | null;
  company_name?: string | null;
  portfolio_id?: number | null;
  portfolio_name?: string | null;
  portfolio_manager_id?: number | null;
  asset_name?: string | null;
  asset_manager_id?: number | null;
  address?: string | null;
  installation_location?: string | null;

  last_calibration_at?: string | null;
  next_calibration_due?: string | null;
  calibration_interval_months?: number | null;
  has_calibration_certificate?: boolean;
  certificate_url?: string | null;
  certificate_name?: string | null;
};

export type CreateDevicePayload = {
  dev_eui: string;
  app_key: string;
  device_profile_key: string;
  portfolio_id: number;
  asset_id: number;
  installation_location: string | null;
  reporting_interval_seconds: number;
  last_calibration_at: string;
  calibration_interval_months: number;
};

export type UpdateDevicePayload = {
  asset_id: number;
  installation_location: string | null;
  reporting_interval_seconds: number;
  calibration_interval_months: number;
};

export type CalibrationPayload = {
  calibrationDate: string;
  calibratedBy: string;
  certificate: File | null;
};

export type CertificateUploadResponse = {
  certificate_url: string;
  certificate_name: string;
};

export type DeviceListResponse = {
  items: DeviceItem[];
  total: number;
  page: number;
  page_size: number;
};

export type SensorMetric = {
  id: number;
  device_id: number;
  metric_type: string;
  value: number;
  recorded_at: string;
  received_at: string;
};

export type MetricListResponse = {
  items: SensorMetric[];
  total: number;
  page: number;
  page_size: number;
};

export type SensorEvent = {
  id: number;
  device_id: number;
  event_type: string;
  recorded_at: string;
  received_at: string;
};

export type EventListResponse = {
  items: SensorEvent[];
  total: number;
  page: number;
  page_size: number;
};

export type DeviceProfile = {
  profile_key: string;
  device_model: string;
};

export type DeviceProfileListResponse = {
  items: DeviceProfile[];
};

export type DeviceStatistics = {
  total: number;
  online: number;
  offline: number;
  never_seen: number;
};

export const DEVICE_EUI_LENGTH = 16;
export const DEVICE_APP_KEY_LENGTH = 32;

// Per-model reporting interval values (seconds). First value of each list
// is always the default (10 minutes).
export const REPORTING_SECONDS_BY_MODEL: Record<string, number[]> = {
  "milesight-am103":          [300, 600, 900, 1800, 3600],
  "milesight-am104":          [300, 600, 900, 1800, 3600],
  "milesight-am308":          [300, 600, 900, 1800],
  "milesight-am319":          [300, 600, 900],
  "milesight-am319-hcho-ir":  [300, 600, 900],
  "milesight-am319-hcho-ir-w":[300, 600, 900],
  "milesight-am319-o3":       [300, 600, 900, 1800],
};

// Union of all model-allowed values; used as fallback when profile_key is unknown.
const REPORTING_SECONDS_FALLBACK = [300, 600, 900, 1800, 3600];

export const REPORTING_INTERVAL_DEFAULT = "600";

function formatReportingLabel(seconds: number): string {
  if (seconds === 3600) return "1 hour";
  return `${seconds / 60} minutes`;
}

export function getReportingOptionsForModel(
  profileKey: string,
  ensureValue?: string,
): { value: string; label: string }[] {
  const seconds = REPORTING_SECONDS_BY_MODEL[profileKey] ?? REPORTING_SECONDS_FALLBACK;
  const options = seconds.map((s) => ({ value: String(s), label: formatReportingLabel(s) }));

  if (ensureValue && !options.some((o) => o.value === ensureValue)) {
    const numeric = Number(ensureValue);
    if (Number.isFinite(numeric) && numeric > 0) {
      options.push({ value: ensureValue, label: formatReportingLabel(numeric) });
    }
  }

  return options;
}

export const CALIBRATION_INTERVAL_OPTIONS = [
  { value: "3", label: "3 months" },
  { value: "6", label: "6 months" },
  { value: "12", label: "12 months" },
  { value: "18", label: "18 months" },
  { value: "24", label: "24 months" },
];

export const CALIBRATION_INTERVAL_DEFAULT = "12";

export const CERTIFICATE_MAX_SIZE = 10 * 1024 * 1024;
export const CERTIFICATE_ALLOWED_TYPES = ["application/pdf"];

export const DEVICE_APP_KEY_DEFAULT = "5572404C696E6B4C6F52613230313823";

export const APP_KEY_DEFAULT_PROFILE_KEYS = [
  "milesight-am103",
  "milesight-am104",
  "milesight-am308",
  "milesight-am319-o3",
  "milesight-am319-hcho-ir",
  "milesight-am319-hcho-ir-w",
] as const;

export function isAppKeyAutofillModel(profileKey: string): boolean {
  return (APP_KEY_DEFAULT_PROFILE_KEYS as readonly string[]).includes(profileKey);
}

export function sanitizeAppKeyInput(raw: string): string {
  let out = "";
  let hexCount = 0;
  for (const ch of raw) {
    if (ch === "-") {
      out += ch;
      continue;
    }
    if (/[0-9A-Za-z]/.test(ch) && hexCount < DEVICE_APP_KEY_LENGTH) {
      out += ch;
      hexCount += 1;
    }
  }
  return out;
}
